import { useCallback, useEffect, useMemo, useState } from 'react';

import analyticsService from '../../../../services/analyticsService';
import getDateRange from '../../../../utils/getDateRange';

import type { IntentDistributionResponse, StudentIntentDistribution } from './types';

type UseIntentDistributionSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

type LoadedFilters = { classId: string; period: string };

// The answer is kept with the filters it was asked for: nothing cancels a request, so the answer on
// screen can be the one of filters the teacher has already left.
type LoadedDistribution = { data: IntentDistributionResponse | null; filters: LoadedFilters };

type UseIntentDistributionSectionReturn = {
  data: IntentDistributionResponse | null;
  error: string | null;
  /** Filters `students` was loaded for, or null while there is nothing loaded to report. */
  loadedFor: LoadedFilters | null;
  loading: boolean;
  students: StudentIntentDistribution[];
};

function useIntentDistributionSection({
  allCourseIds,
  classValue,
  groupCourseMap,
  timePeriodValue,
}: UseIntentDistributionSectionProps): UseIntentDistributionSectionReturn {
  const [loaded, setLoaded] = useState<LoadedDistribution | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const currentCourseIds = useMemo(() => {
    return classValue === 'all' ? allCourseIds : groupCourseMap[classValue] || [];
  }, [classValue, allCourseIds, groupCourseMap]);

  const { startDate: currentStartDate, endDate: currentEndDate } = useMemo(() => {
    return getDateRange(timePeriodValue);
  }, [timePeriodValue]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result: IntentDistributionResponse = await analyticsService.getIntentDistribution({
        courseIds: currentCourseIds,
        startDate: currentStartDate,
        endDate: currentEndDate,
      });

      setLoaded({ data: result, filters: { classId: classValue, period: timePeriodValue } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoaded(null);
    } finally {
      setLoading(false);
    }
  }, [classValue, currentCourseIds, currentStartDate, currentEndDate, timePeriodValue]);

  useEffect(() => {
    if (allCourseIds.length > 0) {
      fetchData();
    } else {
      setLoading(false);
      setLoaded({ data: null, filters: { classId: classValue, period: timePeriodValue } });
    }
  }, [allCourseIds.length, classValue, fetchData, timePeriodValue]);

  const data = loaded?.data ?? null;

  const students = useMemo(() => {
    return data?.students || [];
  }, [data]);

  return {
    data,
    error,
    loadedFor: loaded?.filters ?? null,
    loading,
    students,
  };
}

export default useIntentDistributionSection;
